// RSA-PSS-SHA256 signer for Kalshi REST requests, running entirely inside
// the browser via SubtleCrypto. Output is byte-identical to the Node version
// in apps/executor/src/adapters/kalshi/sign.ts (same message format, same
// salt length, same base64 encoding) so the executor can forward signed
// headers without re-signing or peeking at the user's key.

const SALT_LENGTH = 32;

export class KalshiKeyImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'KalshiKeyImportError';
  }
}

function base64ToBytes(b64: string): Uint8Array {
  const clean = b64.replace(/\s+/g, '');
  const bin = atob(clean);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToBase64(b: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < b.length; i++) bin += String.fromCharCode(b[i]!);
  return btoa(bin);
}

// Accepts a PKCS#8-encoded PEM (the format Kalshi's CLI exports by default).
// Rejects PKCS#1 ("BEGIN RSA PRIVATE KEY") and encrypted PEMs with an
// actionable error — converting those in the browser would mean shipping a
// PKCS#1-to-PKCS#8 ASN.1 translator which is not worth the bytes.
export async function importKalshiKey(pem: string): Promise<CryptoKey> {
  const trimmed = pem.trim();
  if (trimmed.includes('BEGIN RSA PRIVATE KEY')) {
    throw new KalshiKeyImportError(
      'PKCS#1 key detected. Re-export as PKCS#8 with: openssl pkcs8 -topk8 -in key.pem -out key-pkcs8.pem -nocrypt',
    );
  }
  if (trimmed.includes('ENCRYPTED PRIVATE KEY')) {
    throw new KalshiKeyImportError(
      'Encrypted private key detected. Decrypt first with: openssl pkcs8 -in key.pem -out key-decrypted.pem',
    );
  }
  const match = trimmed.match(
    /-----BEGIN PRIVATE KEY-----\s*([A-Za-z0-9+/=\s]+?)\s*-----END PRIVATE KEY-----/,
  );
  if (!match) {
    throw new KalshiKeyImportError(
      'Could not find a PKCS#8 PEM block. Expected "-----BEGIN PRIVATE KEY-----".',
    );
  }
  const raw = base64ToBytes(match[1]!);
  try {
    return await crypto.subtle.importKey(
      'pkcs8',
      raw as BufferSource,
      { name: 'RSA-PSS', hash: 'SHA-256' },
      false,
      ['sign'],
    );
  } catch (err) {
    throw new KalshiKeyImportError(`PKCS#8 import rejected by SubtleCrypto: ${String(err)}`);
  }
}

export interface KalshiSignedHeaders {
  keyId: string;
  signature: string;
  timestamp: string;
}

// Mirrors apps/executor/src/adapters/kalshi/sign.ts:buildHeaders.
// IMPORTANT: `path` must be the request path that Kalshi sees, including
// the leading "/trade-api/v2" prefix and any query string. The executor
// forwards these headers verbatim, so they must already be correct.
export async function signKalshi(
  key: CryptoKey,
  keyId: string,
  method: string,
  path: string,
): Promise<KalshiSignedHeaders> {
  const timestamp = Date.now().toString();
  const message = timestamp + method.toUpperCase() + path;
  const sig = await crypto.subtle.sign(
    { name: 'RSA-PSS', saltLength: SALT_LENGTH },
    key,
    new TextEncoder().encode(message) as BufferSource,
  );
  return {
    keyId,
    signature: bytesToBase64(new Uint8Array(sig)),
    timestamp,
  };
}
